/**
 * BottomSheet — reusable mobile-native bottom sheet.
 *
 * Features:
 *  - Body scroll lock (page stays still under the sheet)
 *  - Sheet body is the only scroll surface (overscroll-behavior: contain)
 *  - Swipe-down-to-close: only activates when the body is at scrollTop 0
 *  - Tap backdrop to close
 *  - Escape key to close (desktop)
 *  - Two-part header: drag handle row + optional title/actions row
 *
 * Props:
 *  open       {boolean}        — controls visibility
 *  onClose    {() => void}     — called on any close gesture
 *  title      {string|node}    — optional title shown in header
 *  subtitle   {string|node}    — optional subtitle below title (italic)
 *  actions    {node}           — optional nodes placed left of the close button
 *  maxHeight  {string}         — CSS max-height, default '92dvh'
 *  children   {node}           — sheet body content
 *
 * Usage — migrate any raw `round-sheet-backdrop / round-sheet` markup to this
 * component. Rounds.js and StatusTransitionSheet.js still use the old pattern
 * and will be migrated in their own sweep passes.
 */
import { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icons';
import { useBodyScrollLock } from './hooks';

export default function BottomSheet({
  open,
  onClose,
  title,
  subtitle,
  actions,
  ariaLabel,    // explicit accessible name for node titles (e.g. plain scientific name)
  maxHeight = '92dvh',
  children,
}) {
  const sheetRef  = useRef(null);
  const bodyRef   = useRef(null);
  const dragRef   = useRef(null); // { startY, startTime, takingOver }
  const titleId   = useId(); // stable id for aria-labelledby

  // Lock page scroll while open
  useBodyScrollLock(open);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Swipe-to-close — uses addEventListener with passive:false so we can
  // call preventDefault and stop the page from scrolling mid-drag.
  useEffect(() => {
    if (!open || !sheetRef.current) return;
    const sheet = sheetRef.current;

    function handleStart(e) {
      dragRef.current = {
        startY: e.touches[0].clientY,
        startTime: Date.now(),
        takingOver: false,
      };
      // Do NOT clear transition here — only do it once we confirm this is a
      // sheet drag (in handleMove). Otherwise every normal scroll touch clears
      // the transition and future snap/close animations can look wrong.
    }

    function handleMove(e) {
      if (!dragRef.current) return;
      const dy         = e.touches[0].clientY - dragRef.current.startY;
      const bodyScroll = bodyRef.current?.scrollTop ?? 0;

      if (!dragRef.current.takingOver) {
        // Only take over when the body is scrolled to the top AND user drags down
        if (bodyScroll > 0 || dy <= 0) return;
        dragRef.current.takingOver = true;
        // Clear transition only now that we're confirmed as a sheet drag
        sheet.style.transition = 'none';
      }
      e.preventDefault(); // prevent page scroll while dragging the sheet
      sheet.style.transform = `translateY(${Math.max(0, dy)}px)`;
    }

    function handleEnd(e) {
      if (!dragRef.current) return;
      const dy       = e.changedTouches[0].clientY - dragRef.current.startY;
      const elapsed  = Date.now() - dragRef.current.startTime;
      const velocity = dy / Math.max(elapsed, 1);
      const wasSheet = dragRef.current.takingOver;
      dragRef.current = null;

      if (!wasSheet) return;

      if (dy > 80 || velocity > 0.5) {
        onClose();
      } else {
        // Snap back
        sheet.style.transition = 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)';
        sheet.style.transform  = 'translateY(0)';
      }
    }

    sheet.addEventListener('touchstart', handleStart, { passive: true });
    sheet.addEventListener('touchmove',  handleMove,  { passive: false });
    sheet.addEventListener('touchend',   handleEnd);
    return () => {
      sheet.removeEventListener('touchstart', handleStart);
      sheet.removeEventListener('touchmove',  handleMove);
      sheet.removeEventListener('touchend',   handleEnd);
    };
  }, [open, onClose]);

  if (!open) return null;

  // aria-labelledby when title is a plain string (safe to wire to an id).
  // For node titles: the caller should pass ariaLabel with the plain-text version.
  // Final fallback: 'Dialog' so the element is never completely unnamed.
  const hasStringTitle = title && typeof title === 'string';
  const resolvedAriaLabel = hasStringTitle
    ? undefined                        // covered by aria-labelledby
    : (ariaLabel || (title ? undefined : 'Dialog'));

  return createPortal(
    <div className="bottom-sheet-backdrop" onClick={onClose}>
      <div
        className="bottom-sheet"
        style={{ maxHeight }}
        ref={sheetRef}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasStringTitle ? titleId : undefined}
        aria-label={resolvedAriaLabel}
      >
        {/* Row 1 — centered drag handle */}
        <div className="bottom-sheet-handle-row">
          <div className="bottom-sheet-handle" />
        </div>

        {/* Row 2 — title / subtitle / actions / close (only when title or actions present) */}
        {(title || actions) && (
          <div className="bottom-sheet-title-row">
            {title && (
              <div className="bottom-sheet-title-block">
                <p
                  className="bottom-sheet-title-text"
                  id={hasStringTitle ? titleId : undefined}
                >
                  {title}
                </p>
                {subtitle && <p className="bottom-sheet-subtitle">{subtitle}</p>}
              </div>
            )}
            {actions && (
              <div className="bottom-sheet-title-actions">{actions}</div>
            )}
            <button className="bottom-sheet-close" onClick={onClose} aria-label="Close" title="Close">
              <Icon name="x" size={18} />
            </button>
          </div>
        )}

        {/* Scrollable body — only this container scrolls */}
        <div className="bottom-sheet-body" ref={bodyRef}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
